import * as Yup from "yup";
import {PaymentInterval, CourseCycle, DiscountUnit, NewCourseLessonInput, CourseInstructorIdInput, CourseNumberFormat} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {apolloClient} from "../../common/ApolloWrapper";
import {IS_COURSE_NUMBER_AVAILABLE} from "../course.graphql";
import {
    COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR,
    CourseLateEntryPricingModeValue,
    CourseMinimumLessonCountSource,
} from "../courseLateEntryPricing";
import {CourseCreateFormValues} from "./CourseCreateInitialValues";

const {boolean, string} = Yup;

export default Yup.object<CourseCreateFormValues>().shape({
    courseTypeId: Yup.string().required(() => i18n.containers.courses.CourseCreate.courseType.errorMsg()),
    locationId: Yup.string().required(() => i18n.containers.courses.CourseCreate.courseLocation.errorMsg()),
    venueId: Yup.string().required(() => i18n.containers.courses.CourseCreate.courseVenue.errorMsg()),
    instructors: Yup.array<CourseInstructorIdInput>()
        .nullable()
        .required(() => i18n.containers.courses.CourseCreate.courseTrainer.errorMsg()),
    fee: Yup.number().required(() => i18n.containers.courses.CourseCreate.courseFee.errorMsg()),
    paymentInterval: Yup.string()
        .oneOf(Object.values(PaymentInterval))
        .required(() => i18n.containers.courses.CourseCreate.courseFee.errorMsg()),
    vat: Yup.number().required(() => i18n.containers.courses.CourseCreate.courseTax.errorMsg()),
    discount: Yup.number()
        .min(0, () => i18n.containers.courses.CourseCreate.courseDiscount.errors.positiveValue())
        // .moreThan(
        //     0,
        //     i18n.containers.courses.CourseCreate.courseDiscount.errors.positiveValue(),
        // )
        .when("discountUnit", {
            is: DiscountUnit.Percentage,
            then: Yup.number().max(100, () => i18n.containers.courses.CourseCreate.courseDiscount.errors.maxPercent()),
        })
        .nullable(),
    discountUnit: Yup.string(),
    includedEntranceFee: Yup.number().nullable(),
    minAttendees: Yup.number().required(() => i18n.containers.courses.CourseCreate.courseAttendeeLimitMin.errorMsg()),
    maxAttendees: Yup.number().required(() => i18n.containers.courses.CourseCreate.courseAttendeeLimitMax.errorMsg()),
    maxAssistantsPerLesson: Yup.number().min(0).integer().nullable(),
    maxWaitlist: Yup.number().nullable(),
    customInvoiceDueDays: Yup.number().integer().min(-365).max(365).nullable(),
    lateEntryPricingMode: Yup.string()
        .oneOf(Object.values(CourseLateEntryPricingModeValue))
        .required(),
    minimumPartialBookingLessonCount: Yup.number()
        .typeError(COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR)
        .integer(COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR)
        .min(1, COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR)
        .nullable()
        .when("minimumPartialBookingLessonCountSource", {
            is: CourseMinimumLessonCountSource.CourseOverride,
            then: Yup.number()
                .typeError(
                    COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR,
                )
                .integer(
                    COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR,
                )
                .min(
                    1,
                    COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR,
                )
                .required(
                    COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR,
                ),
        }),
    startDateTime: Yup.date().required(() => i18n.containers.courses.CourseCreate.courseStartDateTime.errorMsg()),
    endDateTime: Yup.date()
        .min(Yup.ref("startDateTime"), () => i18n.containers.courses.CourseCreate.courseEndDateTime.endTimeBeforeStartShort())
        .required(() => i18n.containers.courses.CourseCreate.courseEndDateTime.errorMsg()),
    cycle: Yup.mixed()
        .oneOf(Object.values(CourseCycle))
        .required(() => i18n.containers.courses.CourseCreate.courseCycle.errorMsg()),
    annotation: Yup.string(),
    websiteInfo: Yup.string(),
    additionalShortInfo: Yup.string()
        .max(64, () => i18n.containers.courses.CourseCreate.additionalShortInfo.errorMsgMaxLength())
        .nullable(),
    lessons: Yup.array<NewCourseLessonInput>().of(
        Yup.object({
            venueId: Yup.string().required(() => i18n.containers.courses.CourseCreate.courseLessons.venue.errorMsg()),
            locationId: Yup.string().required(() => i18n.containers.courses.CourseCreate.courseLessons.venue.errorMsg()),
            instructors: Yup.array<CourseInstructorIdInput>().required(() => i18n.containers.courses.CourseCreate.courseLessons.instructor.errorMsg()),
            startDateTime: Yup.date().required(() => i18n.containers.courses.CourseCreate.courseLessons.startTime.errorMsg()),
            endDateTime: Yup.date()
                .min(Yup.ref("startDateTime"), () => i18n.containers.courses.CourseCreate.courseEndDateTime.endTimeBeforeStart())
                .required(() => i18n.containers.courses.CourseCreate.courseLessons.endTime.errorMsg()),
        }),
    ),
    isActive: boolean().notRequired(),
    showInWeb: boolean().notRequired(),
    allowMonthlyPayment: boolean().notRequired(),
    courseId: string().notRequired().nullable(), // only needed for the min-max age check
    customCourseNumberPrefix: string()
        .max(32, () => i18n.containers.courses.CourseCreate.customCourseNumberPrefix.max())
        .test(
            "customCourseNumberPrefix",
            () => i18n.containers.courses.CourseCreate.customCourseNumberPrefix.courseNumberAllreadyTaken(),
            // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
            async function (value) {
                if (value !== undefined) {
                    const {data, errors} = await apolloClient.query({
                        query: IS_COURSE_NUMBER_AVAILABLE,
                        // Immer den Server fragen: aus dem Cache kam nach dem ersten Kurs weiter "frei",
                        // ein zweiter Kurs mit derselben Nummer ging in derselben Sitzung durch (Babysplash, 09/2026)
                        fetchPolicy: "network-only",
                        variables: {
                            customCourseNumberPrefix: value,
                            courseId: this.parent.courseId ?? "",
                        },
                    });

                    if (errors) {
                        console.log("customCourseNumberPrefix errors: ", errors);
                    }

                    return data.isCourseNameUnique;
                }

                return true;
            },
        )
        .when("courseNumberFormat", {
            is: CourseNumberFormat.CustomPrefix,
            then: string().required(() => i18n.containers.courses.CourseCreate.customCourseNumberPrefix.errorMsg()),
        }),
});
